import * as React from 'react';

import "./social-dashboard-table.scss";

import {cs}                             from "@Common/react/chain-services";
import {consumeContext}                 from "@Common/react/context";
import {Load2}                          from "@Common/react/load2";
import {UseState}                       from "@Common/react/use-state";
import {Pagination}                     from "@Common/ui-components/heavy/common/design2/data-table/pagination";
import {PageList}                       from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {PageSize}                       from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {getPageSummary}                 from "@Common/ui-components/heavy/common/design2/data-table/get-page-summary";
import {FontAwesomeIcon}                from "@fortawesome/react-fontawesome";
import {SocialDashboardCard}            from "./social-dashboard-card";

const PAGE_SIZES = [5, 10]

const ILK_COLUMN = {
    ACTION: 'action',
    DATE: 'date',
    TEXT: 'text',
    MESSAGE: 'message' 
}

export const columns = [
    {label: "+", accessor: "", ilk: ILK_COLUMN.ACTION, sortable: false},
    {label: "POST CONTENT", accessor: "shareBody", ilk: ILK_COLUMN.MESSAGE, sortable: true},
    {label: "SHARED FROM", accessor: "shareLocation", ilk: ILK_COLUMN.TEXT, sortable: true},
    {label: "DATE SHARED", accessor: "dateShared", ilk: ILK_COLUMN.DATE, sortable: true},
]

export const SocialDashboardTable = ({data, sortColumn, sortAscending}) => cs(
    consumeContext("apis"),
    ["groupedDataInit", ({}, next) => next(sortedTableData(groupData(data), sortColumn.value, sortAscending.value))],
    ["isExpanded", ({}, next) => UseState({next, initValue: []})],  
    ["pageSize", ({}, next) => UseState({next, getInitValue: () => PAGE_SIZES[0]})],
    ["pagination", ({pageSize, groupedDataInit}, next) => Pagination({
        _key: JSON.stringify(pageSize),
        pageSize: pageSize.value,
        rows: groupedDataInit,
        next,
    })],
    ["displayedRows", ({pagination, groupedDataInit}, next) => next(pagination?.active ? pagination.getDisplayedRows() : groupedDataInit)],
    ["postIds", ({displayedRows}, next) => next(requestPostMetrics(displayedRows))],
    ["postData", ({apis, postIds}, next) => !postIds ? next() : Load2({
        _key: postIds,
        fetch: () => apis.next.social.getPostMetrics({ids: postIds}),
        next,
    })],   
    ["groupedData", ({postData, displayedRows}, next) => (!postData || postData?.loading) ? (
         next(displayedRows)
    ) : (next(matchData(postData.value, displayedRows))
    )],
    ({groupedDataInit, groupedData, isExpanded, pageSize, pagination}) => (
        <TableOrPlaceholder 
            columns={columns}
            groupedDataInit={groupedDataInit} 
            groupedData={groupedData} 
            sortColumn={sortColumn} 
            sortAscending={sortAscending}
            isExpanded={isExpanded}
            data={data}
            pageSize={pageSize}
            pagination={pagination}
        />
    )
);

const TableOrPlaceholder = ({
    columns, 
    groupedDataInit, 
    groupedData, 
    sortColumn, 
    sortAscending, 
    isExpanded, 
    data, 
    pageSize, 
    pagination
}) => {
    if(!groupedData[0]) {
        return (
            <div className='placeholder' xd-qa-type="label" xd-qa-name="Posts you've shared to your social media account(s) from the Xpressdocs app will appear here.">
                Posts you've shared to your social media account(s) from the Xpressdocs app will appear here.
            </div>
        )
    }
    
    return (
        <div className='pseudo-padding'>
            <table>
                <TableHead columns={columns} groupedData={groupedData} isExpanded={isExpanded} sortColumn={sortColumn} sortAscending={sortAscending} />
                <TableBody columns={columns} groupedData={groupedData} isExpanded={isExpanded} data={data} />
            </table>
            <div className="controls">
                    {
                        PageSize({
                            sizes: PAGE_SIZES,
                            state: pageSize,
                            pageSizeMessage: "",
                        })
                    }
                    {
                        <>
                            {
                                pagination.active
                                ? 
                                <div className="pagination">
                                    {PageList(pagination)}
                                </div>
                                :
                                null
                            }
                            {
                                getPageSummary({
                                    pageIndex: pagination.state.value,
                                    pageSize: pageSize.value,
                                    count: groupedDataInit.length
                                })
                            }
                        </>
                    }
                </div>
        </div>
    )
}

const TableHead = ({columns, groupedData, isExpanded, sortColumn, sortAscending}) => {
    return (
        <thead>
            <tr>
                {
                    columns.map((column, i) => (
                        column.sortable
                        ?
                        TableHeaderSortable({column, groupedData, sortColumn, sortAscending, i})
                        :
                        TableHeader({column, i, groupedData, isExpanded})
                    ))
                }
            </tr>
        </thead>
    )
}

const TableRowExpanded = ({groupedData, data, isExpanded, index}) => {
    
    if(!isExpanded.value.includes(groupedData[index][0]['idGroupPost'])) {
        return null
    } 
    
    return (
        <tr className='tr-expanded' xd-qa-type="card" xd-qa-name={"Expanded Row " + index}>
            <td colSpan={columns.length}>
            {
                groupedData[index]?.map((post, j) => {
                    return (
                        <React.Fragment key={j}>
                            {SocialDashboardCard({post, data, index: j})}
                        </React.Fragment>
                    )
                })
            }
            </td>
        </tr>
    )
}

const TableRow = ({columns, row, index, groupedData, isExpanded}) => {
    if(!row) {
        return null
    }
    const datum = row[0]
    const idGroupPost = groupedData[index][0]['idGroupPost']
    const expanded = isExpanded.value
    const currentlyExpanded = expanded.includes(idGroupPost)

    return (       
        <tr key={`${datum.idPrimary}_${index}`}
            onClick={(e) => {
                e.stopPropagation()                    
                currentlyExpanded
                ?
                expanded.splice(expanded.indexOf(idGroupPost))
                :
                expanded.push(idGroupPost)

                isExpanded.onChange(expanded)
            }}
            xd-qa-type="card"
            xd-qa-name={"Row " + index}
        >
        {
            columns.map((column, j) => {
                const key = `${column.accessor}_${index}_${j}`
                switch(column.ilk) {                    
                    case ILK_COLUMN.DATE:
                        return TableDatumDate({column, datum, key})
                    case ILK_COLUMN.MESSAGE:
                        return TableDatumMessage({column, datum, key})
                    case ILK_COLUMN.ACTION:
                        return TableDatumAction({index, key, groupedData, isExpanded})
                    case ILK_COLUMN.TEXT:
                    default:
                        return TableDatumText({column, datum, key})
                }
            })
        }
        </tr> 
    )
}

const TableBody = ({columns, groupedData, data, isExpanded}) => {
    
    return (
       <tbody>
        {
            groupedData?.map((row, index) => {
                const idGroupPost = groupedData[index][0]['idGroupPost']
                return (
                    <React.Fragment key={index}>                    
                        {TableRow({columns, row, index, groupedData, isExpanded})}
                        {
                            isExpanded.value.includes(idGroupPost) 
                            && 
                            TableRowExpanded({groupedData, data, isExpanded, index})
                        }                
                    </React.Fragment>
                )
            })
        }
       </tbody> 
    )
}

const TableDatumDate = ({column, datum, key}) => {
    const val = formattedDate(datum[column.accessor]["date"]) || "--"
    return <td key={key} xd-qa-type="text" xd-qa-name="Date">{val}</td>
}

const TableDatumMessage = ({column, datum, key}) => {
    const message = datum[column.accessor]["message"] || ""
    return <td key={key} className='message' xd-qa-type="text" xd-qa-name="Message">{message}</td>
}

const TableDatumAction = ({index, key, groupedData, isExpanded}) => {
    const idGroupPost = groupedData[index][0]['idGroupPost']
    const expanded = isExpanded.value
    const currentlyExpanded = expanded.includes(idGroupPost)
    return (
        <td key={key} className='action' xd-qa-type="button" xd-qa-name={currentlyExpanded ? "Collapse" : "Expand"}>
            <i  className={`icon-expand fa fa-xl ${currentlyExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i> 
        </td>
    )
}

const TableDatumText = ({column, datum, key}) => {
    const val = datum[column.accessor]?? "--"
    return <td key={key} xd-qa-type="text" xd-qa-name="Shared From">{mapShareLocation(val)}</td>
}

const mapShareLocation = (location) => {
    switch(location.toLowerCase()) {
        case 'apm':
        case 'apm2':
            return 'APM'
        case 'xpresso':
        default:
            return 'MAIN'
    }
}

const TableHeaderSortable = ({column, groupedData, sortColumn, sortAscending, i}) => {   
    return (
        <th key={`${column.accessor}_${i}`}
            className='sortable'
            onClick={(e) => {
                e.stopPropagation()
                const ascending = !sortAscending.value                 
                const sorted = sortedTableData(groupedData, column.accessor, ascending)
                sortColumn.onChange(column.accessor)
                sortAscending.onChange(ascending)
                groupedData = sorted
            }}
            xd-qa-type="icon"
            xd-qa-name={column.label}
        >
            {column.label}      
            <FontAwesomeIcon 
                className={
                    sortColumn.value === column.accessor
                    ?
                    "selected"
                    :
                    ""
                }
                icon={require("@fortawesome/free-solid-svg-icons")
                    [iconTableHeader(column, sortColumn, sortAscending)]
                }
                xd-qa-type="icon"
                xd-qa-name={column.label + " Sort"}
            />
        </th>
    )
}

const TableHeader = ({column, i, groupedData, isExpanded}) => {
    if(ILK_COLUMN.ACTION == column.ilk) {
        const atLeastOneExpanded = isExpanded.value.length > 0
        return (
            <th key={`${column.accessor}_${i}`} 
                className='action'
                onClick={(e) => {
                    e.stopPropagation()

                    if(atLeastOneExpanded) {
                        isExpanded.onChange([])
                        return
                    }

                    const expandAll = []
                    for(let i = 0; i < groupedData.length; i++) {
                        expandAll.push(groupedData[i][0]['idGroupPost'])
                    }
                    isExpanded.onChange(expandAll)                   
                }}
                xd-qa-type="button"
                xd-qa-name={atLeastOneExpanded ? "Collapse" : "Expand"}
            >
                <i className={
                    atLeastOneExpanded
                    ?
                    "fa fa-minus"
                    :
                    "fa fa-plus"
                    }
                >
                </i> 
            </th>
        )
    }
    return (
        <th key={`${column.accessor}_${i}`} className={column.accessor} xd-qa-type="label" xd-qa-name={column.label}>{column.label}</th>
    )
}

const groupData = (data) => {
    const groupedColumn = []
    let idGroupCurrent
    let groupRowCurrent
    for(let i = 0; i < data.value?.length; i++) {
        const idGroup = data.value[i]["idGroupPost"]
        if(idGroup !== idGroupCurrent) {
            if(groupRowCurrent) {
                groupedColumn.push(groupRowCurrent)
            }

            idGroupCurrent = idGroup
            const groupRow = Array(data.value[i])
            groupRowCurrent = groupRow

            continue
        }
        groupRowCurrent.push(data.value[i])
    }
    groupedColumn.push(groupRowCurrent)

    return groupedColumn
}

const requestPostMetrics = (groupedData) => {
    if(!groupedData) {
        return
    }

    if(0 === groupedData.length) {
        return
    }

    if(!groupedData[0]) {
        return
    }

    const requestIds = []
    const POST_METRIC_REQUEST_MAX = 100
    let maximumReached = false;
    for(let i = 0; i < groupedData.length; i++) {
        for(let j = 0; j < groupedData[i].length; j++) {
            if(!(requestIds.length < POST_METRIC_REQUEST_MAX)) {
                maximumReached = true
                break
            }
            requestIds.push(groupedData[i][j]['idPrimary'])
        }
        if(maximumReached) {
            break
        }
    }
    return requestIds.join()
}

export const sortedTableData = (data, field, ascending=true) => {
    const asc = ascending ? 1 : -1
    switch(field) {
        case 'shareLocation':
            return [...data].sort((a, b) => {
                return a[0]['shareLocation'].toLowerCase().localeCompare(b[0]['shareLocation'].toLowerCase()) * asc
            })
        case 'shareBody':
            return [...data].sort((a, b) => {
                return a[0]['shareBody']['message'].toLowerCase().localeCompare(b[0]['shareBody']['message'].toLowerCase()) * asc
            })
        default:
            return [...data].sort((a, b) => {
                return (a[0]['idPrimary'] - b[0]['idPrimary']) * asc
            })
    }
}

const matchData = (data, groupedData) => {
    return groupedData.map(groupedArray => {        
        return groupedArray.map(groupedDatum => {
            if(!groupedDatum) { 
                return null
            }   
            const obj = data.find(datum => groupedDatum.idPrimary == datum.idPost)
            return {...groupedDatum, likes: obj?.likes, shares: obj?.shares}             
        })
    })
}

const iconTableHeader = (column, sortColumn, sortAscending) => {
    if(!sortAscending.value) {
        return "faCaretDown" 
    } 
    if(sortColumn.value !== column.accessor) {
        return "faCaretDown"
    }
    return "faCaretUp"
}

const formattedDate = (dateToFormat) => {
    const date = new Date(dateToFormat)    
    const day = date.getDate()
    const month = monthAbbreviations[date.getMonth()]
    const year = date.getFullYear()
    const text = `${day} ${month}, ${year}`
    return text
}

const monthAbbreviations = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
]